import type { CodeOutcome, ScanCode } from "@hobbyshopify/backend-client";

/** GS1 mod-10: the last digit checks the rest. */
function hasValidCheckDigit(digits: string): boolean {
  const body = digits.slice(0, -1);
  const sum = [...body].reverse().reduce((total, digit, i) => total + Number(digit) * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === Number(digits.at(-1));
}

/** A zero-suppressed UPC-E, as the UPC-A it stands for; null when it is not one. */
function expandUpcE(digits: string): string | null {
  const [system, d1, d2, d3, d4, d5, d6, check] = [...digits];
  if (system !== "0" && system !== "1") return null;
  const middle =
    d6 === "0" || d6 === "1" || d6 === "2"
      ? `${d1}${d2}${d6}0000${d3}${d4}${d5}`
      : d6 === "3"
        ? `${d1}${d2}${d3}00000${d4}${d5}`
        : d6 === "4"
          ? `${d1}${d2}${d3}${d4}00000${d5}`
          : `${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  const upcA = `${system}${middle}${check}`;
  return hasValidCheckDigit(upcA) ? upcA : null;
}

/**
 * What the merchant typed or a barcode gun sent, as a code the scan can carry:
 * a 12-digit UPC-A, an 8-digit EAN-8 or UPC-E, a 13-digit EAN. Null when it is
 * none of those or its check digit is wrong.
 */
export function barcodeToCode(raw: string): ScanCode | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 12 && hasValidCheckDigit(digits)) return { type: "upc", value: digits };
  if (digits.length === 13 && hasValidCheckDigit(digits)) return { type: "ean", value: digits };
  if (digits.length === 8) {
    if (hasValidCheckDigit(digits)) return { type: "ean", value: digits };
    if (expandUpcE(digits)) return { type: "upc", value: digits };
  }
  return null;
}

/** The sentence for a barcode field that holds something other than a barcode; null when it is empty or valid. */
export function barcodeProblem(raw: string): string | null {
  if (!raw.trim() || barcodeToCode(raw)) return null;
  return "That is not a valid UPC or EAN barcode. Check the digits, or take a photo instead.";
}

/** Why the photo is needed, for a barcode that named no item. */
export function codeOutcomeText(outcome: CodeOutcome): string {
  switch (outcome) {
    case "unknown":
      return "We couldn't find this barcode. Take a photo of the box to identify it.";
    case "conflict":
      return "Shops disagree on what this barcode is. Take a photo of the box to settle it.";
    case "unavailable":
      return "The barcode lookup is down right now. Try again, or take a photo of the box.";
  }
}

/** The retail formats a sealed box carries. */
const RETAIL_FORMATS = ["upc_a", "upc_e", "ean_13", "ean_8"];

type Detector = { detect(image: ImageBitmapSource): Promise<{ rawValue: string; format: string }[]> };

/**
 * Reads a retail barcode out of a photo, where the browser can (Chrome and Edge,
 * Android). Null where it cannot, or when the photo shows none: the barcode
 * field stays empty and the scan goes ahead on the photo alone.
 */
export async function detectBarcode(file: Blob): Promise<string | null> {
  const ctor = (globalThis as { BarcodeDetector?: new (o: { formats: string[] }) => Detector }).BarcodeDetector;
  if (!ctor || typeof createImageBitmap === "undefined") return null;
  try {
    const bitmap = await createImageBitmap(file);
    const found = await new ctor({ formats: RETAIL_FORMATS }).detect(bitmap);
    bitmap.close();
    return found.find((code) => RETAIL_FORMATS.includes(code.format))?.rawValue ?? null;
  } catch {
    return null;
  }
}
